// src/services/jobService.js
import { db } from "../firebase/config";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

// Get all jobs for a customer
export const getJobs = async (customerId) => {
  try {
    const jobsRef = collection(db, "jobs");
    const q = query(
      jobsRef,
      where("customerId", "==", customerId),
      orderBy("date", "desc")
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting jobs:", error);
    throw error;
  }
};

/**
 * Fetch all jobs where the given driver UID is in `directDriverIds`.
 * Used by the DriverQueue admin page to populate the unified queue
 * (events + direct-only jobs) and by driverQueueService reconciliation.
 */
export const getJobsWithDirectDriver = async (driverUid) => {
  if (!driverUid) throw new Error("driverUid is required");
  const ref = collection(db, "jobs");
  const q = query(ref, where("directDriverIds", "array-contains", driverUid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Get a single job
export const getJob = async (customerId, jobId) => {
  try {
    const jobRef = doc(db, "jobs", jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      return null;
    }

    const jobData = jobDoc.data();

    // Verify this job belongs to the customer
    if (jobData.customerId !== customerId) {
      return null;
    }

    return {
      id: jobDoc.id,
      ...jobData,
    };
  } catch (error) {
    console.error("Error getting job:", error);
    throw error;
  }
};

// Create a new job
export const createJob = async (customerId, jobData) => {
  try {
    const jobsRef = collection(db, "jobs");

    const newJob = {
      ...jobData,
      customerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(jobsRef, newJob);

    return docRef.id;
  } catch (error) {
    console.error("Error creating job:", error);
    throw error;
  }
};

// Derive the customer's display name for denormalisation onto jobs/{id}.
// Priority mirrors scripts/enrich-jobs-with-customer.mjs EXACTLY, so a job
// stamped at creation and a job fixed by the backfill script end up with the
// same value — re-running the backfill over new jobs is a no-op, not a change.
// This repo's canonical customer record uses `name` (see doc/data-model.md);
// firstName/lastName only show up in older/imported records.
export const deriveCustomerName = (c) => {
  if (!c) return "";
  const first = (c.firstName || "").trim();
  const last = (c.lastName || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  return (c.name || "").trim();
};

// Customer docs in the wild have phone fields under several names.
// Pick the first non-empty one in priority order — same order as the
// backfill script, for the same reason as deriveCustomerName above.
export const deriveCustomerPhone = (c) => {
  if (!c) return "";
  const candidates = [
    c.phoneNumber,
    c.phone,
    c.mobile,
    c.mobileNumber,
    c.telNumber,
    c.tel,
  ];
  for (const v of candidates) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

// Seed the top-level jobs/{id} document for a job, id-aligned with the
// embedded customer.jobs[] copy (setDoc with the job's own id, NOT addDoc).
// New jobs are written to customer.jobs[] AND here, so everything that targets
// jobs/{id} — the fitter portal, the rooms mirror, and driver assignment —
// has a document to read/update. Merge so re-running is safe.
//
// `customer` (optional third arg) denormalises the customer's name + phone onto
// the job at creation time. The fitter portal cannot read the `customer`
// collection (Firestore Rules deny it — fitters only see jobs they're assigned
// to), so a job that isn't carrying these fields shows a blank card. Passing
// the customer here is what stops NEW jobs being born blank;
// scripts/enrich-jobs-with-customer.mjs remains the tool for OLD ones.
export const seedTopLevelJob = async (customerId, job, customer) => {
  if (!job?.id) return;
  const jobRef = doc(db, "jobs", job.id);

  const payload = {
    ...job,
    customerId,
    assignedDriverIds: job.assignedDriverIds || [],
    directDriverIds: job.directDriverIds || [],
    rooms: job.rooms || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // Only stamp when the caller actually supplied a customer. Omitting the
  // fields (rather than writing "") keeps `{ merge: true }` safe to re-run:
  // a caller that doesn't know the customer can never blank a value the
  // backfill script already filled in.
  if (customer) {
    payload.customerName = deriveCustomerName(customer);
    payload.customerPhone = deriveCustomerPhone(customer);
  }

  await setDoc(jobRef, payload, { merge: true });
};

// Update a job
export const updateJob = async (customerId, jobId, jobData) => {
  try {
    // First check if job exists and belongs to this customer
    const existingJob = await getJob(customerId, jobId);

    if (!existingJob) {
      throw new Error("Job not found or does not belong to this customer");
    }

    const jobRef = doc(db, "jobs", jobId);

    // Remove id from the data to update
    const { id, ...dataToUpdate } = jobData;

    const updatedData = {
      ...dataToUpdate,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(jobRef, updatedData);

    return true;
  } catch (error) {
    console.error("Error updating job:", error);
    throw error;
  }
};

/**
 * Add a driver to a job's `directDriverIds` array — the admin-managed list
 * of drivers who have permission to view this job's rooms WITHOUT a
 * corresponding calendar event.
 *
 * Two-array model on jobs/{id}:
 * - assignedDriverIds: auto-derived from calendar events (calendarService
 *   recomputes on every event create/update/delete that touches driverId).
 * - directDriverIds:   admin-managed via the job page (this function and
 *   removeDirectDriverFromJob).
 *
 * Firestore Rules grant a driver access to the job when their UID appears
 * in EITHER array (see firestore-rules-driver-role.md).
 *
 * Uses Firestore arrayUnion → idempotent, atomic, safe under concurrent
 * writes. No transaction needed.
 *
 * @param {string} jobId
 * @param {string} driverUid  Firebase Auth UID of the driver
 * @returns {Promise<void>}
 */
export const addDirectDriverToJob = async (jobId, driverUid) => {
  if (!jobId || !driverUid) {
    throw new Error("jobId and driverUid are required");
  }
  const jobRef = doc(db, "jobs", jobId);
  await updateDoc(jobRef, {
    directDriverIds: arrayUnion(driverUid),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Remove a driver from a job's `directDriverIds` array. The driver may still
 * retain access if they're listed in `assignedDriverIds` (i.e. assigned to
 * at least one calendar event on this job).
 *
 * @param {string} jobId
 * @param {string} driverUid
 * @returns {Promise<void>}
 */
export const removeDirectDriverFromJob = async (jobId, driverUid) => {
  if (!jobId || !driverUid) {
    throw new Error("jobId and driverUid are required");
  }
  const jobRef = doc(db, "jobs", jobId);
  await updateDoc(jobRef, {
    directDriverIds: arrayRemove(driverUid),
    updatedAt: serverTimestamp(),
  });
};

// Delete a job
export const deleteJob = async (customerId, jobId) => {
  try {
    // First check if job exists and belongs to this customer
    const existingJob = await getJob(customerId, jobId);

    if (!existingJob) {
      throw new Error("Job not found or does not belong to this customer");
    }

    const jobRef = doc(db, "jobs", jobId);
    await deleteDoc(jobRef);

    return true;
  } catch (error) {
    console.error("Error deleting job:", error);
    throw error;
  }
};
